import { useState, useContext, useRef, useEffect } from 'react';
import { ThemeContext } from '../Context';
import { GuardiaAccessChecker } from './GuardiaAccessChecker';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
import { ReactComponent as GuardiaLogo } from '../../assets/logo_guardia.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import IconButton from '@mui/material/IconButton';
import { logAction, formatReportNumber, LOG_ACTIONS, contentLengthLog } from '../../utils/logClient';

function GuardiaWrite({ rubric, reportNumber, reportYear, onSave, onCancel }) {
  const { theme } = useContext(ThemeContext);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedRubric, setGeneratedRubric] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('initial'); // 'initial', 'generated', 'modifying'
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [listenError, setListenError] = useState('');
  const [guardiaAccess, setGuardiaAccess] = useState({ hasAccess: false, reason: '' });
  const [listenAccess, setListenAccess] = useState({ hasAccess: false, reason: '' });
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);

  // Vérifier l'accès GuardIA au chargement
  useEffect(() => {
    const checkGuardiaAccess = async () => {
      const [writeAccess, voiceAccess] = await Promise.all([
        GuardiaAccessChecker.checkFeatureAccess('write'),
        GuardiaAccessChecker.checkFeatureAccess('listen')
      ]);
      setGuardiaAccess(writeAccess);
      setListenAccess(voiceAccess);
    };
    
    checkGuardiaAccess();
  }, []);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt du MediaRecorder (GuardiaWrite):', error);
        }
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => {
          try {
            track.stop();
          } catch (error) {
            console.warn('Erreur lors de l\'arrêt d\'une piste audio (GuardiaWrite):', error);
          }
        });
        audioStreamRef.current = null;
      }
    };
  }, []);

  // Déterminer si le thème est sombre
  const isDark = theme === 'dark';

  const extractJSON = (responseText) => {
    try {
      return JSON.parse(responseText);
    } catch (e) {
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          return JSON.parse(jsonMatch[0]);
        } catch (e2) {
          throw new Error('Format JSON invalide dans la réponse');
        }
      }
      throw new Error('Aucun objet JSON trouvé dans la réponse');
    }
  };

  const handleGenerate = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }

    if (!prompt.trim()) {
      setError('Veuillez entrer une description pour générer la rubrique.');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: prompt,
          profile: 'GuardiaWrite'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        try {
          const parsedResponse = extractJSON(data.response);

          // Vérifier que la structure attendue est présente
          if (!parsedResponse.title || !parsedResponse.content) {
            throw new Error('Structure JSON incomplète - titre ou contenu manquant');
          }

          setGeneratedRubric(parsedResponse);
          setPhase('generated');
          
          // Log de la génération GuardIA Write
          logAction(LOG_ACTIONS.GUARDIA_WRITE, formatReportNumber(reportNumber, reportYear), {
            action: 'generation',
            titreGenere: parsedResponse.title,
            ...contentLengthLog(parsedResponse.content || '')
          });
          
          setPrompt(''); // Vider le textarea
        } catch (parseError) {
          console.error('Erreur de parsing JSON:', parseError.message);
          console.error('Réponse brute:', data.response);
          setError(`Erreur lors de l'analyse de la réponse de GuardIA : ${parseError.message}. Veuillez réessayer.`);
        }
      } else {
        setError(`Erreur lors de la génération : ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API GuardIA:', error);
      setError('Erreur de connexion à GuardIA. Vérifiez que le serveur est démarré.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleModify = async () => {
    if (!guardiaAccess.hasAccess) {
      setError(guardiaAccess.reason);
      return;
    }

    if (!prompt.trim()) {
      setError('Veuillez entrer les modifications à apporter.');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Combiner la proposition actuelle avec les modifications demandées
      const modificationPrompt = `Voici la proposition actuelle de rubrique :

Titre : ${generatedRubric.title}
Contenu : ${generatedRubric.content}

Modifications demandées : ${prompt}

Veuillez adapter la rubrique selon ces modifications.`;

      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: modificationPrompt,
          profile: 'GuardiaWrite'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        try {
          const parsedResponse = extractJSON(data.response);

          if (!parsedResponse.title || !parsedResponse.content) {
            throw new Error('Structure JSON incomplète - titre ou contenu manquant');
          }

          setGeneratedRubric(parsedResponse);
          setPhase('generated');
          
          // Log de la modification GuardIA Write
          logAction(LOG_ACTIONS.GUARDIA_WRITE, formatReportNumber(reportNumber, reportYear), {
            action: 'modification',
            titreModifie: parsedResponse.title,
            ...contentLengthLog(parsedResponse.content || '', rubric?.content)
          });
          
          setPrompt(''); // Vider le textarea
        } catch (parseError) {
          console.error('Erreur de parsing JSON lors de la modification:', parseError.message);
          console.error('Réponse brute (modification):', data.response);
          setError(`Erreur lors de l'analyse de la réponse de GuardIA : ${parseError.message}. Veuillez réessayer.`);
        }
      } else {
        setError(`Erreur lors de la modification : ${data.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur lors de l\'appel à l\'API GuardIA:', error);
      setError('Erreur de connexion à GuardIA. Vérifiez que le serveur est démarré.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseProposition = () => {
    if (generatedRubric) {
      onSave({
        title: generatedRubric.title,
        content: generatedRubric.content
      });
    }
  };

  const appendTranscriptToPrompt = (transcript) => {
    setPrompt(prev => {
      const trimmedPrev = prev.trim();
      const combined = trimmedPrev ? `${trimmedPrev} ${transcript}` : transcript;
      return combined.trim();
    });
  };

  const guardiaListen = async (audioBlob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setListenError('Aucun audio détecté. Veuillez réessayer.');
      return;
    }

    setListenError('');
    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'guardia-write-recording.webm');
      formData.append('language', 'fr');

      const token = sessionStorage.getItem('authToken');
      const response = await fetch('/api/guardia/listen', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      let data = null;
      try {
        const clonedResponse = response.clone();
        data = await response.json();
        if (!response.ok && clonedResponse) {
          const rawText = await clonedResponse.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (GuardiaWrite):', rawText);
          }
        }
      } catch (jsonError) {
        console.error('Réponse JSON invalide de guardiaListen (GuardiaWrite):', jsonError);
        try {
          const rawText = await response.text();
          if (rawText) {
            console.error('Réponse brute guardiaListen (GuardiaWrite) après échec JSON:', rawText);
          }
        } catch (rawError) {
          console.error('Impossible de lire la réponse brute guardiaListen (GuardiaWrite):', rawError);
        }
      }

      if (!response.ok || !data || !data.success) {
        const errorMessage = data?.message || data?.error || 'Impossible de transcrire l\'audio.';
        throw new Error(errorMessage);
      }

      const transcript = (data.transcript || '').trim();
      if (transcript) {
        appendTranscriptToPrompt(transcript);
      } else {
        setListenError('Transcription vide. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Erreur de transcription GuardIA (GuardiaWrite):', error);
      setListenError(error.message || 'Erreur inattendue lors de la transcription.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => {
        try {
          track.stop();
        } catch (error) {
          console.warn('Erreur lors de l\'arrêt d\'une piste audio (GuardiaWrite):', error);
        }
      });
      audioStreamRef.current = null;
    }
  };

  const stopListening = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      setIsListening(false);
      try {
        recorder.stop();
      } catch (error) {
        console.warn('Erreur lors de l\'arrêt du MediaRecorder (GuardiaWrite):', error);
      }
    } else {
      setIsListening(false);
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      stopAudioStream();
    }
  };

  const startListening = async () => {
    if (!listenAccess.hasAccess) {
      setListenError(listenAccess.reason);
      return;
    }

    if (isListening || isTranscribing) {
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setListenError('La capture audio n\'est pas supportée par ce navigateur.');
      return;
    }

    try {
      setListenError('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      mediaRecorder.addEventListener('error', (event) => {
        console.error('Erreur MediaRecorder (GuardiaWrite):', event.error);
        setListenError(event.error?.message || 'Erreur lors de l\'enregistrement audio.');
        setIsListening(false);
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        stopAudioStream();
      });

      mediaRecorder.addEventListener('stop', () => {
        setIsListening(false);
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        stopAudioStream();

        if (audioBlob.size === 0) {
          setListenError('Aucun audio détecté. Veuillez réessayer.');
          return;
        }

        guardiaListen(audioBlob);
      });

      mediaRecorder.start();
      setIsListening(true);
    } catch (error) {
      console.error('Erreur lors de l\'activation du micro GuardIA (GuardiaWrite):', error);
      stopAudioStream();
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsListening(false);

      if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
        setListenError('Accès au micro refusé. Veuillez autoriser l\'accès pour utiliser la dictée.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        setListenError('Aucun micro n\'a été détecté.');
      } else {
        setListenError(error.message || 'Impossible de démarrer l\'enregistrement audio.');
      }
    }
  };

  const toggleMicrophone = () => {
    if (!listenAccess.hasAccess) {
      alert(listenAccess.reason);
      return;
    }
    
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {/* Textarea pour le prompt */}
        <TextField
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          placeholder={
            phase === 'initial'
              ? "Entrez la description de votre rubrique..."
              : "Entrez les modifications à apporter à la proposition..."
          }
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(221, 160, 221, 0.1)',
              '& fieldset': {
                borderColor: '#9370DB'
              },
              '&:hover fieldset': {
                borderColor: '#8A2BE2'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8A2BE2'
              }
            }
          }}
        />

      {/* Bouton Générer/Modifier avec bouton micro */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          startIcon={
            <GuardiaLogo
              style={{
                width: '16px',
                height: '16px',
                color: 'white'
              }}
            />
          }
          onClick={phase === 'initial' ? handleGenerate : handleModify}
          disabled={!prompt.trim() || isGenerating}
          sx={{
            backgroundColor: isDark ? '#c084fc' : '#800080',
            color: 'white',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#9932cc',
            },
            '& .MuiButton-startIcon': {
              color: 'white'
            },
            '&.Mui-disabled': {
              backgroundColor: '#ccc',
              color: 'white'
            }
          }}
        >
          {isGenerating ? (
            <>
              <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
              {phase === 'initial' ? 'Génération en cours...' : 'Modification en cours...'}
            </>
          ) : (
            phase === 'initial' ? 'Générer avec GuardIA' : 'Modifier avec GuardIA'
          )}
        </Button>

        {/* Bouton micro et indicateur d'écoute */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={toggleMicrophone}
            disabled={!listenAccess.hasAccess || (isTranscribing && !isListening)}
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: !listenAccess.hasAccess
                ? (isDark ? '#555' : '#ccc')
                : isListening
                ? (isDark ? '#ff5722' : '#f44336')
                : (isDark ? '#2d3f4c' : 'grey.100'),
              color: !listenAccess.hasAccess
                ? (isDark ? '#7f8c8d' : 'grey.400')
                : isListening
                ? '#fff'
                : (isDark ? '#95a5a6' : 'grey.600'),
              transition: 'all 0.2s ease',
              boxShadow: isListening ? '0 0 0 2px rgba(244, 67, 54, 0.3)' : 'none',
              animation: isListening ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
                },
                '50%': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 0 0 4px rgba(244, 67, 54, 0.2)',
                },
                '100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 2px rgba(244, 67, 54, 0.3)',
                },
              },
              '&:hover': !listenAccess.hasAccess ? {} : {
                backgroundColor: isListening
                  ? (isDark ? '#d84315' : '#d32f2f')
                  : (isDark ? '#5a6c7d' : 'grey.200'),
                transform: isListening ? 'scale(1.05)' : 'scale(1.05)',
              },
              '&.Mui-disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
                color: isDark ? '#7f8c8d' : 'grey.400'
              }
            }}
          >
            {isListening ? <MicOffIcon /> : (isTranscribing ? <CircularProgress size={18} sx={{ color: isDark ? '#95a5a6' : 'grey.600' }} /> : <MicIcon />)}
          </IconButton>
          {isListening && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.75,
              borderRadius: '20px',
              backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
              backdropFilter: 'blur(8px)',
              border: `1px solid ${isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'}`,
              boxShadow: isDark
                ? '0 2px 8px rgba(244, 67, 54, 0.15)'
                : '0 2px 8px rgba(244, 67, 54, 0.08)',
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#f44336',
                mr: 1,
                animation: 'recordingPulse 1.5s infinite',
                '@keyframes recordingPulse': {
                  '0%': { transform: 'scale(1)', opacity: 1 },
                  '50%': { transform: 'scale(1.3)', opacity: 0.7 },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: isDark ? '#ff8a80' : '#d32f2f',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                }}
              >
                Écoute en cours...
              </Typography>
            </Box>
          )}
          {isTranscribing && !isListening && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0.75,
              borderRadius: '20px',
              backgroundColor: isDark ? 'rgba(33, 150, 243, 0.08)' : 'rgba(25, 118, 210, 0.06)',
              border: `1px solid ${isDark ? 'rgba(79, 195, 247, 0.25)' : 'rgba(25, 118, 210, 0.2)'}`,
              boxShadow: isDark
                ? '0 2px 8px rgba(25, 118, 210, 0.2)'
                : '0 2px 8px rgba(33, 150, 243, 0.1)'
            }}>
              <CircularProgress size={14} sx={{ mr: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.75rem',
                  color: isDark ? '#4fc3f7' : '#1976d2',
                  fontWeight: 500,
                  letterSpacing: '0.02em'
                }}
              >
                Transcription en cours...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {listenError && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)',
            color: isDark ? '#ff8a80' : 'error.main',
            '& .MuiAlert-icon': {
              color: isDark ? '#ff8a80' : 'error.main'
            }
          }}
        >
          {listenError}
        </Alert>
      )}

      {/* Message d'erreur */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            backgroundColor: 'rgba(244, 67, 54, 0.15)',
            color: '#ff6b6b',
            '& .MuiAlert-icon': {
              color: '#ff6b6b'
            }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Proposition générée */}
      {generatedRubric && (
        <Fade in={true} timeout={300}>
          <Paper
            elevation={1}
            sx={{
              mt: 2,
              mb: 2,
              p: 2,
              backgroundColor: isDark ? 'rgba(192, 132, 252, 0.1)' : 'rgba(221, 160, 221, 0.3)',
              borderRadius: 2,
              border: `1px solid ${isDark ? '#c084fc' : '#9370DB'}`,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Titre de la proposition */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 'bold',
                mb: 1,
                color: isDark ? '#c084fc' : '#800080',
                fontSize: '0.75rem'
              }}
            >
              Proposition de rubrique :
            </Typography>

            {/* Contenu de la proposition */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: isDark ? '#c084fc' : '#800080',
                  mb: 1
                }}
              >
                Titre : {generatedRubric.title}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  color: isDark ? '#ecf0f1' : '#333',
                  lineHeight: 1.6
                }}
              >
                {generatedRubric.content}
              </Typography>
            </Box>

            {/* Boutons d'action */}
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleUseProposition}
                sx={{
                  backgroundColor: '#27ae60',
                  color: 'white',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#2ecc71',
                  },
                  '& .MuiButton-startIcon': {
                    color: 'white'
                  }
                }}
              >
                Utiliser cette proposition
              </Button>
            </Box>

            {/* Message d'avertissement */}
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: 'block',
                textAlign: 'center',
                color: isDark ? '#95a5a6' : '#666',
                fontSize: '0.7rem',
                fontStyle: 'italic'
              }}
            >
              GuardIA peut commettre des erreurs, vérifiez toujours les propositions de rubrique qu'il vous donne.
            </Typography>
          </Paper>
        </Fade>
      )}
    </Box>
  );
}

export default GuardiaWrite;
